import dayjs from "dayjs";
import { capitalizeString } from "./helpers";

const ALL_MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

/**
 * Calculates the current fiscal year string for a company based on its fiscal year start and end months.
 *
 * The fiscal year is always labeled by its **ending year**.
 * - If the fiscal year falls within a single calendar year (e.g., Jan → Dec, Apr → Nov),
 *   it always returns the current year.
 * - If the fiscal year crosses into the next calendar year (e.g., Oct → Sep, May → Apr):
 *   - If today's month is greater than or equal to the start month, the fiscal year ends next year.
 *   - Otherwise, it ends this year.
 *
 * Month names must match abbreviations from the `ALL_MONTHS` array:
 * `["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"]`.
 * Input is case-insensitive (`"jan"`, `"JAN"`, and `"Jan"` are all valid).
 *
 * @param {string[]} currentCompanyMonths - An array of two month abbreviations (case-insensitive):
 *   - `currentCompanyMonths[0]`: The start month of the fiscal year.
 *   - `currentCompanyMonths[1]`: The end month of the fiscal year.
 *   Example: `["Oct", "Sep"]` means the fiscal year runs from October to September.
 *
 * @returns {string} The current fiscal year label (e.g., `"2026 Fiscal Year"`).
 *
 * @throws {Error} If one or both provided months are not valid abbreviations.
 *
 * @example
 * // Today = Feb 2026
 * calculateCurrentFiscalYear(["Jan", "Dec"]);
 * // → "2026 Fiscal Year"
 *
 * @example
 * // Today = Nov 2025
 * calculateCurrentFiscalYear(["Oct", "Sep"]);
 * // → "2026 Fiscal Year"
 *
 * @example
 * // Today = Mar 2026
 * calculateCurrentFiscalYear(["Oct", "Sep"]);
 * // → "2026 Fiscal Year"
 */

export const calculateCurrentFiscalYear = (currentCompanyMonths: string[]): string => {
  const targetMonths = currentCompanyMonths.map((month) => capitalizeString(month));

  if (!targetMonths.every((month) => ALL_MONTHS.includes(month))) {
    throw new Error(
      `Invalid month(s) provided. Expected abbreviations: ${ALL_MONTHS.join(", ")}`
    );
  }

  const todayMonth = dayjs().get("month");
  const companyStartMonth = ALL_MONTHS.indexOf(targetMonths[0]);
  const companyEndMonth = ALL_MONTHS.indexOf(targetMonths[1]);

  const currentYear = dayjs().get("year");

  if (companyStartMonth > companyEndMonth) {
    if (todayMonth >= companyStartMonth) {
      return `${currentYear + 1} Fiscal Year`;
    }
  }

  return `${currentYear} Fiscal Year`;
};
